import { Mutex } from 'async-mutex'
import { LIMITS } from '@weblite/permission'
import { Timestamp } from '@weblite/ts-action'
import throttle from 'lodash.throttle'
import {
  DeleteDraft,
  Draft,
  reqDeleteDraft,
  reqSetDraft,
  reqSyncDraft,
  SetDraft,
  SyncDraft,
} from '@weblite/ts-action/draft'
import { request } from '$setup/ws'
import { draftsStorage } from './storage'
import * as sync from './offline-sync'
import { dateWithOffset } from '$logic/client-device/time-offset.service'

export const getDraft = draftsStorage.getFromRedux
const mutex = new Mutex()

export async function syncDrafts(): Promise<void> {
  const { deletedDrafts, del, addedDrafts } = await mutex.runExclusive(
    async () => {
      const syncObject = await sync.getAll()
      const storageDrafts = syncObject.storedSyncDrafts
        .map(({ roomId }) => getDraft(roomId))
        .filter(Boolean)

      const deleted = syncObject.storedSyncDrafts.filter(
        ({ roomId }) => !getDraft(roomId),
      )

      return {
        deletedDrafts: deleted,
        addedDrafts: storageDrafts,
        del: syncObject.del,
      }
    },
  )

  const deleted = deletedDrafts.reduce(
    (prev, current) => {
      return { ...prev, [current.roomId]: current.date }
    },
    {} as Record<string, Timestamp>,
  )

  const res = await request<SyncDraft>(
    reqSyncDraft({ drafts: addedDrafts, deletedDrafts: deleted }),
  )

  if (res.payload) {
    await mutex.runExclusive(del)
    await draftsStorage.clear()
    await draftsStorage.add(res.payload.drafts)
  }
}

export async function setImmediateDraft(
  roomId: string,
  value: Draft['value'],
  date: Timestamp,
): Promise<void> {
  const draft: Draft = { roomId, value, date: date || dateWithOffset() }
  const unsetSync = await mutex.runExclusive(async () => {
    const [unsetFunc] = await Promise.all([
      sync.set(roomId, draft.date),
      draftsStorage.add(draft),
    ])
    return unsetFunc
  })
  await request<SetDraft>(reqSetDraft(draft))
  await mutex.runExclusive(unsetSync)
}

const throttledDraft = throttle(setImmediateDraft, LIMITS.draftThrottleTime, {
  leading: false,
  trailing: true,
})

export const setDraft = throttledDraft
export const flushDraft = throttledDraft.flush.bind(throttledDraft)
export const cancelSetDraft = throttledDraft.cancel.bind(throttledDraft)

export async function deleteDraft(roomId: string): Promise<void> {
  cancelSetDraft()
  const date = dateWithOffset()
  const draft = getDraft(roomId)
  if (!draft) return
  const unsetSync = await mutex.runExclusive(async () => {
    const [unsetFunc] = await Promise.all([
      sync.set(roomId, date),
      draftsStorage.del(roomId),
    ])
    return unsetFunc
  })
  await request<DeleteDraft>(reqDeleteDraft({ roomId }))
  await unsetSync()
}
